import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi, profileApi, onAuthLogout, type Profile } from '@/lib/api';
import { reachGoal, GOAL_LOGIN } from '@/lib/metrika';

interface AuthContextType {
  user: Profile | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: () => void;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Profile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const initAuth = async () => {
      if (authApi.isAuthenticated()) {
        try {
          const profile = await profileApi.getProfile();
          setUser(profile);
        } catch (error) {
          // Раньше чистили tokens на ЛЮБОЙ fail — это ронило сессию при
          // транзиентных ошибках (maintenance/5xx/network). 401 уже
          // обрабатывается в apiRequest (refresh + clearTokens + emit
          // logout event). Здесь — только лог, токены не трогаем; следующий
          // initAuth/refreshUser сделает retry когда backend оживёт.
          console.warn('Failed to load profile on init (will retry):', error);
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, []);

  // Слушаем глобальное событие logout (apiRequest эмитит когда refresh
  // фейлится и юзер фактически разлогинен). Сразу очищаем state, чтобы
  // Header не показывал залогиненный UI пока не дойдёт reload.
  useEffect(() => {
    return onAuthLogout(() => {
      setUser(null);
    });
  }, []);

  // Handle OAuth callback from Steam — обмен одноразового code на токены.
  // Раньше ?access_token=&refresh_token= шли прямо в URL (утекали в Referer/логи).
  // Теперь backend выдаёт single-use code (TTL 60с), мы его меняем по POST.
  useEffect(() => {
    const handleAuthCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const error = urlParams.get('error');

      // Only process if we have auth params
      if (!code && !error) return;

      // Сразу чистим URL — code одноразовый, повторное чтение не нужно,
      // и не даём ему висеть в history/Referer.
      window.history.replaceState({}, document.title, window.location.pathname);

      if (error) {
        console.error('Auth error:', error);
        return;
      }

      if (code) {
        try {
          const { access_token, refresh_token } = await authApi.exchangeAuthCode(code);
          authApi.setTokens(access_token, refresh_token);

          const profile = await profileApi.getProfile();
          setUser(profile);
          reachGoal(GOAL_LOGIN);
          navigate('/profile');
        } catch (err) {
          console.error('Failed to exchange auth code:', err);
        }
      }
    };

    handleAuthCallback();
  }, [navigate]);

  const login = async () => {
    try {
      const { login_url } = await authApi.getSteamLoginUrl();
      window.location.href = login_url;
    } catch (error) {
      console.error('Failed to get Steam login URL:', error);
    }
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      authApi.clearTokens();
      navigate('/');
    }
  };

  const refreshUser = async () => {
    try {
      const profile = await profileApi.getProfile();
      setUser(profile);
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
